/**
 * Pure save-time diff for repeatable-section families (#1501 Option B, commit 2).
 *
 * Extracted from the six `*EditSessionFlush.ts` files, which used to compute
 * this diff interleaved with the HTTP/offline-queue calls in a single pass.
 * The flushes now CONSUME this compute step (compute → execute), and the
 * aggregate save payload builder serializes its output directly — one diff
 * implementation, two consumers.
 *
 * Contract notes (parity with the pre-extraction flushes):
 * - deletes: persisted baseline rows whose id is absent from the session.
 * - creates: session-only rows (temp/negative ids). `clientKey` is the
 *   stringified temp id (stable across retries); rows the caller's
 *   `skipCreate` veto (the racks legacy-synthetic-row rule) are excluded.
 * - updates: persisted session rows whose content key differs from their
 *   baseline row; rows with no baseline counterpart are skipped (they belong
 *   to a teammate's concurrent edit, not this session).
 * - orderRefs: the session's final order as refs — persisted ids stay
 *   numbers, creates appear as their clientKey. Skipped creates are excluded.
 */

export interface RepeatableRowLike {
  id?: number;
}

export interface ComputedCreate<T> {
  clientKey: string;
  /** The session temp id (negative, or the legacy synthetic id). */
  tempId: number | undefined;
  row: T;
}

export interface ComputedUpdate {
  id: number;
  data: Record<string, unknown>;
}

export interface ComputedRepeatableOps<T> {
  deletes: number[];
  creates: Array<ComputedCreate<T>>;
  updates: ComputedUpdate[];
  /** Desired final order: persisted ids (numbers) + create clientKeys (strings). */
  orderRefs: Array<number | string>;
  /** Baseline persisted ids that survive in the session, in baseline order. */
  baselineOrderInSession: number[];
}

export interface ComputeRepeatableOptions<T extends RepeatableRowLike> {
  isPersistedId: (id: number | undefined) => id is number;
  isSessionOnlyId: (id: number | undefined) => boolean;
  /** Canonical content key — identity/timestamps excluded (family-specific). */
  contentKey: (row: T) => string;
  /** Identity keys stripped, returning the update payload (family-specific). */
  stripForUpdate: (row: T) => Record<string, unknown>;
  /**
   * Veto a session-only row's creation (racks: a legacy synthetic row whose
   * content still equals its baseline counterpart is display-only, not a create).
   */
  skipCreate?: (row: T, baseline: T[]) => boolean;
}

export function computeRepeatableOperations<T extends RepeatableRowLike>(
  baseline: T[],
  session: T[],
  opts: ComputeRepeatableOptions<T>,
): ComputedRepeatableOps<T> {
  const baselinePersisted = baseline.filter(row => opts.isPersistedId(row.id));
  const sessionPersistedIds = new Set(
    session.filter(row => opts.isPersistedId(row.id)).map(row => row.id),
  );

  const deletes: number[] = [];
  for (const base of baselinePersisted) {
    if (!sessionPersistedIds.has(base.id)) {
      deletes.push(base.id as number);
    }
  }

  const creates: Array<ComputedCreate<T>> = [];
  const skippedTempIds = new Set<number>();
  session.forEach((row, index) => {
    if (!opts.isSessionOnlyId(row.id)) {
      return;
    }
    if (opts.skipCreate?.(row, baseline)) {
      if (row.id != null) skippedTempIds.add(row.id);
      return;
    }
    creates.push({
      clientKey: row.id != null ? String(row.id) : `new:${index}`,
      tempId: row.id,
      row,
    });
  });

  const updates: ComputedUpdate[] = [];
  for (const row of session) {
    if (!opts.isPersistedId(row.id)) {
      continue;
    }
    const base = baseline.find(item => item.id === row.id);
    if (!base || opts.contentKey(base) === opts.contentKey(row)) {
      continue;
    }
    updates.push({ id: row.id, data: opts.stripForUpdate(row) });
  }

  const createKeyByTempId = new Map<number | undefined, string>(
    creates.map(c => [c.tempId, c.clientKey]),
  );
  const orderRefs: Array<number | string> = [];
  session.forEach((row, index) => {
    if (opts.isPersistedId(row.id)) {
      orderRefs.push(row.id);
      return;
    }
    if (row.id != null && skippedTempIds.has(row.id)) {
      return;
    }
    const key =
      createKeyByTempId.get(row.id) ??
      (row.id != null ? String(row.id) : `new:${index}`);
    orderRefs.push(key);
  });

  const baselineOrderInSession = baselinePersisted
    .map(row => row.id as number)
    .filter(id => sessionPersistedIds.has(id));

  return { deletes, creates, updates, orderRefs, baselineOrderInSession };
}

/**
 * Should a reorder be sent at all? Any create in the final order counts as an
 * order change (parity with the flushes: a created id was always absent from
 * the baseline order, so the arrays never matched).
 */
export function hasOrderChange<T>(ops: ComputedRepeatableOps<T>): boolean {
  if (ops.orderRefs.length === 0) {
    return false;
  }
  if (ops.orderRefs.some(ref => typeof ref === 'string')) {
    return true;
  }
  return (
    JSON.stringify(ops.orderRefs) !== JSON.stringify(ops.baselineOrderInSession)
  );
}
